import type { KeyboardEvent, PointerEvent } from "react";
import { useTranslation } from "react-i18next";
import type { SoloTrack, TrackPlayer } from "../demo/track-player.ts";
import { audioClock } from "../demo/voice.ts";
import { Icon } from "./Icon.tsx";

/**
 * One audio file's player (0.24.0, shared in 0.25.0): ▶, a position bar to drag or
 * tap, the time and the length, ±5 s. Ours rather than the browser's: the same on
 * every browser, and it plays Ogg on iPhone through the voice engine.
 */
export function SoloPlayer({ player, track, at, testId }: { player: TrackPlayer; track: SoloTrack; at: number; testId?: string }) {
  const { t } = useTranslation();
  const mine = player.id === track.id;
  const playing = mine && player.playing;
  const length = mine ? player.duration : (track.duration ?? null);
  const jump = (e: PointerEvent<HTMLDivElement>) => {
    if (!length) return;
    const box = e.currentTarget.getBoundingClientRect();
    const to = ((e.clientX - box.left) / box.width) * length;
    if (mine) player.seek(to);
    else player.cue(track, to);
  };
  const key = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = { ArrowRight: 1, ArrowLeft: -1, PageUp: 10, PageDown: -10 }[e.key];
    if (!step) return;
    e.preventDefault();
    if (mine) player.seek(at + step);
    else player.cue(track, Math.max(0, at + step));
  };
  const move = (by: number) => (mine ? player.seek(at + by) : player.cue(track, Math.max(0, at + by)));
  const pct = length ? `${Math.min(100, (at / length) * 100)}%` : "0%";
  return (
    <div className="vplayer">
      <button
        type="button"
        className={`vplayer-play${playing ? " is-playing" : ""}`}
        aria-label={t(playing ? "demos.voice.pauseVoice" : "demos.voice.playVoice")}
        aria-pressed={playing}
        onClick={() => player.toggle(track)}
      >
        <Icon name={playing ? "pause" : "play"} size={18} />
      </button>
      <div className="vplayer-body">
        <div
          className="vplayer-bar"
          role="slider"
          tabIndex={0}
          aria-label={t("demos.voice.position")}
          aria-valuemin={0}
          aria-valuemax={Math.round(length ?? 0)}
          aria-valuenow={Math.round(at)}
          aria-valuetext={audioClock(at)}
          onPointerDown={jump}
          onKeyDown={key}
        >
          <i style={{ width: pct }} />
          <b style={{ left: pct }} />
        </div>
        <div className="vplayer-times">
          <span className="mono" data-testid={testId}>
            {audioClock(at)}
          </span>
          <span className="muted mono">{length ? audioClock(length) : "–:–"}</span>
        </div>
      </div>
      <div className="vplayer-skips">
        <button type="button" className="btn btn-quiet btn-small mono" aria-label={t("demos.voice.back5")} onClick={() => move(-5)}>
          −5s
        </button>
        <button type="button" className="btn btn-quiet btn-small mono" aria-label={t("demos.voice.on5")} onClick={() => move(5)}>
          +5s
        </button>
      </div>
    </div>
  );
}
